/**
 * The "Underlag" block of a student's detail page (pedagogy spec §3.3.4, §13
 * Q2): what the student has solved on their own and with help, the topics per
 * state, and the started topics with their counts. States and counts only; the
 * server sends no confidence rating, calibration or slip, so there is none to show.
 */
import { useMemo } from "react";
import { Badge, StatTile, Table, type BadgeTone } from "@client/components/ui";
import { useTopicsAndChapters } from "@client/api/topics";
import { useLang, useT } from "@client/i18n";
import { formatNumber } from "@client/lib/format";
import type { StudentEvidence, StudentTopicEvidence, TopicState } from "./types";

const STATE_ORDER: readonly TopicState[] = [
  "not-started",
  "tried",
  "with-help",
  "on-your-own",
  "secure",
];

const STATE_TONE: Record<TopicState, BadgeTone> = {
  "not-started": "neutral",
  tried: "neutral",
  "with-help": "warning",
  "on-your-own": "accent",
  secure: "success",
};

export function StudentEvidenceSection({ evidence }: { evidence: StudentEvidence }) {
  const t = useT();
  const { lang } = useLang();
  const topicsQuery = useTopicsAndChapters();

  const topicNameById = useMemo(() => {
    const names = new Map<string, string>();
    for (const topic of topicsQuery.data?.topics ?? []) names.set(topic.id, topic.name[lang]);
    return names;
  }, [topicsQuery.data, lang]);

  const started = evidence.topics.filter((topic) => topic.state !== "not-started");

  return (
    <section className="mb-8" aria-labelledby="teacher-evidence-title">
      <h2
        id="teacher-evidence-title"
        className="mb-1 font-display text-lg text-ink-900 dark:text-ink-50"
      >
        {t("teacher.detail.evidence.title")}
      </h2>
      <p className="mb-3 text-sm text-ink-600 dark:text-ink-400">
        {t("teacher.detail.evidence.description")}
      </p>

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <StatTile
          label={t("teacher.detail.evidence.unaided")}
          value={formatNumber(evidence.solvedUnaided, lang)}
        />
        <StatTile
          label={t("teacher.detail.evidence.assisted")}
          value={formatNumber(evidence.solvedWithHelp, lang)}
        />
        <StatTile
          label={t("teacher.detail.evidence.retests")}
          value={t("teacher.detail.evidence.retestsValue", {
            passed: formatNumber(evidence.retestsPassed, lang),
            taken: formatNumber(evidence.retestsTaken, lang),
          })}
        />
      </div>

      <h3
        id="teacher-evidence-states"
        className="mb-2 text-sm font-medium text-ink-700 dark:text-ink-200"
      >
        {t("teacher.detail.evidence.statesLabel")}
      </h3>
      <ul aria-labelledby="teacher-evidence-states" className="mb-4 flex flex-wrap gap-2">
        {STATE_ORDER.map((state) => (
          <li key={state}>
            <Badge tone={STATE_TONE[state]}>
              {t(`teacher.detail.evidence.state.${state}`)}
              <span className="ml-1.5 font-semibold tabular-nums">
                {formatNumber(evidence.states[state], lang)}
              </span>
            </Badge>
          </li>
        ))}
      </ul>

      <h3 className="mb-2 text-sm font-medium text-ink-700 dark:text-ink-200">
        {t("teacher.detail.evidence.topics.title")}
      </h3>
      <Table<StudentTopicEvidence>
        columns={[
          {
            key: "topic",
            header: t("teacher.detail.evidence.topics.topic"),
            cell: (row) => (
              <span>
                <span className="text-xs text-ink-500 dark:text-ink-400">
                  {t("teacher.chapterLabel")} {row.chapter} ·{" "}
                </span>
                {topicNameById.get(row.topicId) ?? row.topicId}
              </span>
            ),
          },
          {
            key: "state",
            header: t("teacher.detail.evidence.topics.state"),
            cell: (row) => (
              <Badge tone={STATE_TONE[row.state]}>
                {t(`teacher.detail.evidence.state.${row.state}`)}
              </Badge>
            ),
          },
          {
            key: "items",
            header: t("teacher.detail.evidence.topics.items"),
            align: "right",
            cell: (row) => formatNumber(row.items, lang),
          },
          {
            key: "unaidedItems",
            header: t("teacher.detail.evidence.topics.unaided"),
            align: "right",
            cell: (row) => formatNumber(row.unaidedItems, lang),
          },
          {
            key: "assistedOnlyItems",
            header: t("teacher.detail.evidence.topics.assisted"),
            align: "right",
            cell: (row) => formatNumber(row.assistedOnlyItems, lang),
          },
        ]}
        rows={started}
        rowKey={(row) => row.topicId}
        empty={
          <p className="text-sm text-ink-600 dark:text-ink-400">
            {t("teacher.detail.evidence.topics.empty")}
          </p>
        }
      />
    </section>
  );
}
